import posthog from 'posthog-js';
import type { User } from '@/types';

type EventProperties = Record<string, unknown>;
type SurveyCallback = (surveys: unknown, context?: { error?: unknown }) => void;
type PostHogSurveyClient = typeof posthog & {
    displaySurvey?: (surveyId: string, options?: Record<string, unknown>) => void;
    getSurveys?: (callback: SurveyCallback, forceReload?: boolean) => void;
};

let lastIdentifiedUserId: string | null = null;

const normalizeError = (error: unknown) =>
    error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');

export const initializePostHog = () => {
    const posthogProjectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN?.trim();
    const posthogEnabled = import.meta.env.VITE_POSTHOG_ENABLED === 'true';
    const releaseVersion = import.meta.env.VITE_RELEASE_VERSION?.trim();

    window.NoteableAnalytics = noteableAnalytics;

    if (!posthogEnabled || !posthogProjectToken) {
        return;
    }

    posthog.init(posthogProjectToken, {
        api_host: 'https://us.i.posthog.com',
        defaults: '2026-01-30',
        capture_pageview: false,
        autocapture: false,
        capture_exceptions: {
            capture_unhandled_errors: true,
            capture_unhandled_rejections: true,
            capture_console_errors: true,
        },
    });

    window.posthog = posthog;

    if (releaseVersion) {
        posthog.register({
            releaseName: 'noteable-web',
            releaseVersion,
        });
    }
};

export const resetAnalytics = () => {
    window.posthog?.reset();
    lastIdentifiedUserId = null;
};

export const identifyUser = (user: User | null) => {
    const userId = user?.Authenticated && user?.Id != null ? String(user.Id) : null;

    if (!userId) {
        resetAnalytics();
        return;
    }

    if (lastIdentifiedUserId === userId) {
        return;
    }

    window.posthog?.identify(userId, {
        email: user.Email,
        name: user.Name,
        userId: user.Id,
        tenantId: user.TenantId,
        tenantName: user.TenantName,
    });
    lastIdentifiedUserId = userId;
};

export const captureException = (error: unknown, properties?: EventProperties) => {
    window.posthog?.captureException(normalizeError(error), properties);
};

export const capturePageView = (pathname: string) => {
    if (!pathname) {
        return;
    }

    window.posthog?.capture('$pageview', {
        $current_url: window.location.href,
        $pathname: pathname,
    });
};

export const capturePageLeave = (pathname: string) => {
    if (!pathname) {
        return;
    }

    window.posthog?.capture('$pageleave', {
        $current_url: window.location.href,
        $pathname: pathname,
    });
};

export const getSurveys = (callback: SurveyCallback, forceReload?: boolean) => {
    const posthogClient = window.posthog as PostHogSurveyClient | undefined;

    if (typeof posthogClient?.getSurveys !== 'function') {
        return;
    }

    posthogClient.getSurveys(callback, forceReload);
};

export const displaySurvey = (surveyId: string, options?: Record<string, unknown>) => {
    const posthogClient = window.posthog as PostHogSurveyClient | undefined;

    if (typeof posthogClient?.displaySurvey !== 'function') {
        return;
    }

    posthogClient.displaySurvey(surveyId, options);
};

export const registerPostHogUserBridge = () => {
    window.VueBridge?.on('userUpdated', identifyUser);
};

export const noteableAnalytics = {
    captureException,
    capturePageLeave,
    capturePageView,
    displaySurvey,
    getSurveys,
    identifyUser,
    reset: resetAnalytics,
};
